// frontend/src/widgets/ResourceListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-09-05
// Updated:      2026-10-23 (BST)
//
// Description:
//   Resource list styled like AdvancedCustomerListWidget.
//   • Round avatars rendered via PREVIEW ROUTE ONLY to ensure consistency:
//       /api/resources/{id}/avatar/preview?ts=...
//     (Ignores raw avatar_url; avoids cross-container /static path issues and cache).
//   • Column chooser modal via ModalWrapper.
//   • Client-side search & sorting.
//   • Default shows Active resources only.
//     Toggle "Show redacted/inactive" fetches full list (attempts include_inactive=true; falls back safely).
//   • Header row is sticky while scrolling.
//
// Notes:
//   • GET /resources expected via axios baseURL (/api).
//
// Mobile Responsiveness (2026-09-09):
//   • Desktop retains the existing table, sticky sortable headers and Edit action.
//   • Mobile converts the same table rows into labelled resource cards; no duplicate
//     data render tree is mounted.
//   • Mobile shows one complete resource card per internal vertical scroll/snap.
//   • Dynamic mobile height uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary viewport/root sizing;
//       measure the natural first rendered resource card;
//       set the records viewport to exactly one complete card;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   • Mobile toolbar stacks cleanly and adds explicit Sort By + ▲/▼ controls because
//     the desktop table header is hidden.
//   • Column chooser retains visibility controls and adds mobile ↑/↓ ordering.
//   • ResourceEditModal opening/saving/refresh behaviour is unchanged.
//   • Resource filtering, avatar preview routing, search and GET behaviour are unchanged.
//
// Taxonomy integration (2026-10-23 BST):
//   • Resolve cost centre category and subtype names from tenant-scoped API data.
//   • Remove legacy notes-based classification and numeric-ID display.
//   • Preserve sorting, search, column chooser, desktop table and mobile card sizing.
//   • Surface taxonomy loading errors without misrepresenting classifications.
//
// Operational Notes:
//   • Selected columns remain the source of truth for both desktop and mobile.
//   • Mobile labels resolve from the module-level COLUMN_LABELS registry; row cells
//     never depend on a header-callback-local column variable.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";
import ResourceEditModal from "../components/ResourceEditModal.jsx";
import ModalWrapper from "../components/ModalWrapper";

// ---------- Avatar helper ----------
const previewSrc = (id) =>
  id ? `${window.location.origin}/api/resources/${id}/avatar/preview?ts=${Date.now()}` : "";

const nameToInitials = (first, last, email) => {
  const a = (first || "").trim();
  const b = (last || "").trim();
  if (a || b) return `${a[0] || ""}${b[0] || ""}`.toUpperCase() || "—";
  const em = (email || "").trim();
  return em ? em[0].toUpperCase() : "—";
};

const AvatarCircle = ({ id, first_name, last_name, email, size = 36 }) => {
  const [error, setError] = useState(false);
  const src = previewSrc(id);
  const initials = nameToInitials(first_name, last_name, email);

  return (
    <div
      className="rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center overflow-hidden"
      style={{ width: size, height: size }}
      title={`${first_name || ""} ${last_name || ""}`.trim() || email || "Resource"}
    >
      {!error && src ? (
        <img
          src={src}
          alt="avatar"
          className="w-full h-full object-cover"
          onError={() => setError(true)}
        />
      ) : (
        <span className="text-xs text-gray-600 font-semibold select-none">{initials}</span>
      )}
    </div>
  );
};

// ---------- Columns ----------
const ALL_COLUMNS = [
  { key: "avatar", label: "Avatar" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "resource_type", label: "Type" },
  { key: "job_title", label: "Job Title" },
  { key: "employment_status", label: "Status" },
  { key: "active", label: "Active" },
  { key: "is_billable", label: "Billable" },
  { key: "capacity_hours_per_week", label: "Capacity (hrs/wk)" },
  { key: "timezone", label: "Timezone" },
  { key: "location_id", label: "Location ID" },
  { key: "department_id", label: "Department ID" },
  { key: "manager_id", label: "Manager ID" },
  { key: "cost_center_display", label: "Cost Center" },
  { key: "cost_rate", label: "Cost Rate" },
  { key: "default_bill_rate", label: "Bill Rate" },
  { key: "burden_cost", label: "Burden Cost" },
  { key: "identifier", label: "Identifier" },
  { key: "system_role", label: "System Role" },
  { key: "hire_date", label: "Hire Date" },
  { key: "leave_date", label: "Leave Date" },
];

const COLUMN_LABELS = Object.freeze(
  Object.fromEntries(
    ALL_COLUMNS.map((columnDef) => [columnDef.key, columnDef.label])
  )
);

// Defaults
const DEFAULT_VISIBLE = [
  "avatar",
  "name",
  "email",
  "mobile",
  "resource_type",
  "job_title",
  "employment_status",
  "active",
  "is_billable",
  "capacity_hours_per_week",
  "timezone",
  "location_id",
  "cost_center_display",
  "hire_date",
  "leave_date",
];

// ---------- Helpers ----------
const asItems = (data) => Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
const idKey = (value) => value == null ? "" : String(value);
const taxonomyLabel = (item) => String(item?.name || item?.code || "").trim();

function formatDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-GB", { timeZone: "Europe/London" });
  } catch {
    return d;
  }
}

// ---------- Column Chooser Modal ----------
const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) => {
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const from = prev.indexOf(key);
      if (from < 0) return prev;

      const to = from + direction;
      if (to < 0 || to >= prev.length) return prev;

      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="text-sm text-gray-600 mb-3">Tick the columns you want to see.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-auto pr-2">
        {order.map((key) => {
          const columnDef = allColumns.find((candidate) => candidate.key === key);
          if (!columnDef) return null;

          return (
            <div
              key={key}
              className="flex min-w-0 items-center justify-between gap-2 rounded border border-gray-200 px-2 py-2 text-sm"
            >
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() => handleToggle(key)}
                />
                <span className="min-w-0 break-words">{columnDef.label}</span>
              </label>

              <div className="flex shrink-0 gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={order.indexOf(key) <= 0}
                  className="min-h-[36px] min-w-[36px] rounded border border-gray-200 bg-white px-2 font-bold disabled:opacity-40"
                  aria-label={`Move ${columnDef.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={order.indexOf(key) >= order.length - 1}
                  className="min-h-[36px] min-w-[36px] rounded border border-gray-200 bg-white px-2 font-bold disabled:opacity-40"
                  aria-label={`Move ${columnDef.label} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={onClose}
          className="min-h-[44px] w-full sm:w-auto bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(content, portalTarget);
};

// ---------- Main Widget ----------
const ResourceListWidget = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [editing, setEditing] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [taxonomy, setTaxonomy] = useState({ categories: {}, subtypes: {} });
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [taxonomyError, setTaxonomyError] = useState("");

  // Search & columns
  const [searchQuery, setSearchQuery] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);

  // Active-only toggle (default ON)
  const [showAll, setShowAll] = useState(false);

  // Visibility map + order
  const [visibilityMap, setVisibilityMap] = useState(() => {
    const map = {};
    ALL_COLUMNS.forEach((c) => (map[c.key] = DEFAULT_VISIBLE.includes(c.key)));
    return map;
  });
  const [order, setOrder] = useState(() => ALL_COLUMNS.map((c) => c.key));

  const widgetRootRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstResourceRecordRef = useRef(null);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        let res;

        try {
          // Prefer server-side filtering if supported
          res = await api.get("/resources/", { params: { include_inactive: !!showAll } });
        } catch {
          // Fallback if params route isn't supported in this environment
          res = await api.get("/resources/");
        }

        if (!active) return;

        const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setRows(data);
      } catch (e) {
        if (!active) return;
        setErr(e?.response?.data?.detail || e?.message || "Failed to load resources");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [refreshTick, showAll]);

  // Tenant scope comes from the authenticated API session, never from client input.
  useEffect(() => {
    let active = true;
    (async () => {
      setTaxonomyLoading(true);
      setTaxonomyError("");
      try {
        const response = await api.get("/cost-centers/");
        const categories = asItems(response.data);
        const subtypeResponses = await Promise.all(
          categories.map((category) => api.get(`/cost-centers/${category.id}/subtypes`))
        );
        if (!active) return;
        const categoryNames = {};
        const subtypeNames = {};
        categories.forEach((category, index) => {
          categoryNames[idKey(category.id)] = taxonomyLabel(category);
          asItems(subtypeResponses[index].data).forEach((subtype) => {
            // Composite key ensures a subtype is only resolved in its category.
            subtypeNames[`${idKey(category.id)}:${idKey(subtype.id)}`] = taxonomyLabel(subtype);
          });
        });
        setTaxonomy({ categories: categoryNames, subtypes: subtypeNames });
      } catch (error) {
        if (!active) return;
        setTaxonomy({ categories: {}, subtypes: {} });
        setTaxonomyError(error?.response?.data?.detail || error?.message || "Unable to load cost centre taxonomy");
      } finally {
        if (active) setTaxonomyLoading(false);
      }
    })();
    return () => { active = false; };
  }, [refreshTick]);

  const withComputed = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return base.map((r) => {
      const name = [r.first_name, r.last_name].filter(Boolean).join(" ").trim();
      const categoryId = idKey(r.cost_center_id);
      const subtypeId = idKey(r.cost_center_subtype_id);
      const categoryName = categoryId ? taxonomy.categories[categoryId] : "";
      const subtypeName = categoryId && subtypeId
        ? taxonomy.subtypes[`${categoryId}:${subtypeId}`]
        : "";
      const cc = !categoryId
        ? ""
        : categoryName
          ? subtypeId
            ? `${categoryName} > ${subtypeName || "Unknown subtype"}`
            : categoryName
          : taxonomyLoading ? "Loading…" : "Unknown cost centre";
      return {
        ...r,
        __name: name,
        __cost_center_display: cc,
      };
    });
  }, [rows, taxonomy, taxonomyLoading]);

  // Default: only active resources. If showAll is enabled, show everything returned.
  const activeFiltered = useMemo(() => {
    if (showAll) return withComputed;
    return withComputed.filter((r) => r?.active === true);
  }, [withComputed, showAll]);

  const visibleKeys = useMemo(
    () => order.filter((key) => visibilityMap[key]),
    [order, visibilityMap]
  );

  const mobileSortKeys = useMemo(() => {
    if (visibleKeys.includes(sort.key)) return visibleKeys;
    return [sort.key, ...visibleKeys.filter((key) => key !== sort.key)];
  }, [sort.key, visibleKeys]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return activeFiltered;
    const needle = searchQuery.trim().toLowerCase();
    const keys = [
      "__name",
      "email",
      "mobile",
      "resource_type",
      "job_title",
      "identifier",
      "system_role",
      "__cost_center_display",
      "employment_status",
      "timezone",
    ];
    return activeFiltered.filter((r) => keys.some((k) => String(r[k] ?? "").toLowerCase().includes(needle)));
  }, [activeFiltered, searchQuery]);

  const sorted = useMemo(() => {
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    const normalize = (v) =>
      v === undefined || v === null ? "" : typeof v === "string" ? v.toLowerCase() : v;

    return [...filtered].sort((a, b) => {
      let av;
      let bv;

      if (key === "name") {
        av = normalize(a.__name);
        bv = normalize(b.__name);
      } else if (key === "cost_center_display") {
        av = normalize(a.__cost_center_display);
        bv = normalize(b.__cost_center_display);
      } else {
        av = normalize(a[key]);
        bv = normalize(b[key]);
      }

      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sort]);

  const headerClick = (key) => {
    setSort((s) => {
      if (s.key === key) return { key, dir: s.dir === "asc" ? "desc" : "asc" };
      return { key, dir: "asc" };
    });
  };

  const renderSortIndicator = (field) => {
    if (field !== sort.key) return null;
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  const refresh = () => setRefreshTick((t) => t + 1);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = recordsViewportRef.current;
    const firstRecord = firstResourceRecordRef.current;

    if (!root || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstRecord = firstResourceRecordRef.current;

        if (currentFirstRecord) {
          const recordHeight = Math.ceil(
            Math.max(
              currentFirstRecord.getBoundingClientRect().height || 0,
              currentFirstRecord.scrollHeight || 0
            )
          );

          if (Number.isFinite(recordHeight) && recordHeight > 0) {
            viewport.style.height = `${recordHeight}px`;
            viewport.style.minHeight = `${recordHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstRecord) {
      resizeObserver.observe(firstRecord);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    chooserOpen,
    err,
    loading,
    searchQuery,
    showAll,
    sort.dir,
    sort.key,
    sorted.length,
    visibleKeys,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-resource-list-table {
            display: block;
            width: 100%;
          }

          .vx-resource-list-table thead {
            display: none;
          }

          .vx-resource-list-table tbody {
            display: grid;
            gap: 8px;
            width: 100%;
          }

          .vx-resource-list-row {
            display: grid;
            width: 100%;
            min-width: 0;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #fff !important;
            overflow: hidden;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-resource-list-cell {
            display: grid;
            grid-template-columns: minmax(94px, 0.42fr) minmax(0, 1fr);
            gap: 10px;
            align-items: center;
            width: 100%;
            min-width: 0;
            padding: 8px 10px !important;
            border-bottom: 1px solid #f3f4f6;
            overflow-wrap: anywhere;
          }

          .vx-resource-list-cell::before {
            content: attr(data-label);
            color: #6b7280;
            font-size: 10px;
            line-height: 14px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.2px;
          }

          .vx-resource-list-actions {
            display: grid !important;
            grid-template-columns: minmax(94px, 0.42fr) minmax(0, 1fr);
            gap: 10px;
            align-items: center;
            width: 100%;
            min-width: 0;
            padding: 8px 10px !important;
          }

          .vx-resource-list-actions::before {
            content: "Manage";
            color: #6b7280;
            font-size: 10px;
            line-height: 14px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.2px;
            text-align: left;
          }

          .vx-resource-list-records {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-resource-list-records::-webkit-scrollbar {
            display: none;
          }
        }
      `}</style>
      {/* Header */}
      <div className="bg-accent text-white px-3 py-3 flex flex-col gap-3 md:px-4 md:py-2 md:flex-row md:flex-wrap md:justify-between md:items-center">
        <span className="text-sm font-semibold">Search the Resource List</span>

        <div className="grid w-full grid-cols-1 gap-2 md:flex md:w-auto md:flex-wrap md:items-center">
          <label className="min-h-[44px] flex items-center gap-2 text-xs font-semibold select-none md:min-h-0">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="accent-white"
              title="When enabled, includes inactive/redacted resources"
            />
            Show redacted/inactive
          </label>

          <button
            onClick={refresh}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-1 rounded shadow md:min-h-0 md:w-auto"
            title="Refresh list"
          >
            Refresh
          </button>

          <input
            type="text"
            placeholder="Search..."
            className="min-h-[44px] w-full text-sm text-black border border-white bg-white rounded px-2 py-1 md:min-h-0 md:w-48"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="grid grid-cols-[minmax(0,1fr)_44px] gap-2 md:hidden">
            <select
              className="min-h-[44px] min-w-0 rounded border border-white bg-white px-2 text-sm text-black"
              value={sort.key}
              onChange={(e) =>
                setSort((current) => ({
                  key: e.target.value,
                  dir: current.dir,
                }))
              }
              aria-label="Sort resources by"
            >
              {mobileSortKeys.map((key) => (
                <option key={key} value={key}>
                  Sort by: {COLUMN_LABELS[key] || key}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() =>
                setSort((current) => ({
                  ...current,
                  dir: current.dir === "asc" ? "desc" : "asc",
                }))
              }
              className="min-h-[44px] rounded bg-white/90 px-2 text-sm font-bold text-black"
              aria-label={sort.dir === "asc" ? "Sort ascending" : "Sort descending"}
            >
              {sort.dir === "asc" ? "▲" : "▼"}
            </button>
          </div>

          <button
            onClick={() => setChooserOpen(true)}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-1 rounded shadow md:min-h-0 md:w-auto"
            title="Choose Columns"
          >
            Column Chooser
          </button>
        </div>
      </div>

      {taxonomyError ? (
        <div className="break-words px-4 py-2 text-sm text-red-700 bg-red-50 border-b border-red-200">
          Cost centre names unavailable: {String(taxonomyError)}
        </div>
      ) : null}

      {err ? (
        <div className="break-words px-4 py-2 text-sm text-red-700 bg-red-50 border-b border-red-200">{err}</div>
      ) : null}

      {/* Table */}
      <div ref={recordsViewportRef} className="vx-resource-list-records h-auto w-full overflow-visible md:h-full md:overflow-auto">
        <table className="vx-resource-list-table w-full text-sm text-left text-gray-700">
          <thead>
            <tr className="bg-accent text-white select-none">
              {visibleKeys.map((key) => {
                const columnDef = ALL_COLUMNS.find((candidate) => candidate.key === key);
                if (!columnDef) return null;

                // Sticky header fix (same approach as prior widgets)
                const stickyClass = "sticky top-0 z-10 bg-accent";

                return (
                  <th
                    key={key}
                    className={`px-4 py-2 cursor-pointer ${stickyClass}`}
                    onClick={() => headerClick(key)}
                    title="Click to sort"
                  >
                    {columnDef.label}
                    {renderSortIndicator(key)}
                  </th>
                );
              })}
              <th className="px-4 py-2 text-center sticky top-0 z-10 bg-accent">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleKeys.length + 1} className="text-center py-4 text-gray-500 italic">
                  Loading resources...
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={visibleKeys.length + 1} className="text-center py-4 text-gray-500 italic">
                  No resources found.
                </td>
              </tr>
            ) : (
              sorted.map((r, index) => (
                <tr key={r.id} ref={index === 0 ? firstResourceRecordRef : null} className="vx-resource-list-row odd:bg-gray-50 even:bg-white border-b border-gray-100">
                  {visibleKeys.map((key) => {
                    if (key === "avatar") {
                      return (
                        <td key={key} data-label={COLUMN_LABELS[key] || key} className="vx-resource-list-cell px-4 py-2">
                          <AvatarCircle
                            id={r.id}
                            first_name={r.first_name}
                            last_name={r.last_name}
                            email={r.email}
                            size={36}
                          />
                        </td>
                      );
                    }

                    if (key === "name") {
                      return (
                        <td key={key} data-label={COLUMN_LABELS[key] || key} className="vx-resource-list-cell px-4 py-2">
                          {[r.first_name, r.last_name].filter(Boolean).join(" ").trim() || "—"}
                        </td>
                      );
                    }

                    if (key === "cost_center_display") {
                      return (
                        <td key={key} data-label={COLUMN_LABELS[key] || key} className="vx-resource-list-cell px-4 py-2">
                          {r.__cost_center_display || "—"}
                        </td>
                      );
                    }

                    if (key === "hire_date" || key === "leave_date") {
                      return (
                        <td key={key} data-label={COLUMN_LABELS[key] || key} className="vx-resource-list-cell px-4 py-2">
                          {formatDate(r[key])}
                        </td>
                      );
                    }

                    const val = r[key];
                    return (
                      <td key={key} data-label={COLUMN_LABELS[key] || key} className="vx-resource-list-cell px-4 py-2">
                        {val === null || typeof val === "undefined"
                          ? "—"
                          : typeof val === "boolean"
                          ? val
                            ? "Yes"
                            : "No"
                          : String(val)}
                      </td>
                    );
                  })}

                  <td className="vx-resource-list-actions px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-xs px-3 py-1 rounded shadow sm:w-auto"
                      title="Edit Resource"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Column chooser modal */}
      <ColumnChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        allColumns={ALL_COLUMNS}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
      />

      {/* Edit modal */}
      {editing && (
        <ResourceEditModal
          resource={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setRefreshTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
};

export default ResourceListWidget;
